/**
 * Deterministic abstract-image generator.
 *
 * Given a `seed` string (e.g. an article title) it produces a fully described
 * "scene" that the AbstractImage component renders as SVG. The same seed always
 * yields the same scene — no network, storage, or API.
 *
 * The generator is composition-aware rather than purely random: it derives a
 * harmony palette, picks a single off-centre focal point (rule-of-thirds / phi),
 * keeps the opposite quadrant as negative space, and builds the image in depth
 * layers so one element clearly leads. Three vocabularies are supported:
 *   - mesh    : soft blurred colour blobs (calm, modern)
 *   - lowpoly : jittered-grid triangulation, gradient-coloured (crisp, premium)
 *   - bauhaus : a few flat geometric primitives (bold, graphic)
 * An optional fan of leading lines can radiate from the focal point.
 */

export type AbstractVariant = 'mesh' | 'lowpoly' | 'bauhaus'

export const VIEW_W = 400
export const VIEW_H = 300

interface HSL {
  h: number
  s: number
  l: number
}

interface Blob {
  cx: number
  cy: number
  r: number
  fill: string
  opacity: number
  blur: 'far' | 'mid' | 'near'
}

interface Triangle {
  points: string
  fill: string
}

interface BauhausShape {
  kind: 'circle' | 'ring' | 'rect' | 'quarter' | 'bar'
  fill: string
  opacity: number
  // circle / ring / quarter
  cx?: number
  cy?: number
  r?: number
  // rect / bar
  x?: number
  y?: number
  w?: number
  h?: number
  rot?: number
  // quarter wedge path
  d?: string
}

interface Line {
  x1: number
  y1: number
  x2: number
  y2: number
  stroke: string
  opacity: number
  width: number
}

export interface AbstractScene {
  variant: AbstractVariant
  bgFrom: string
  bgTo: string
  gradAngle: number
  blobs: Blob[]
  triangles: Triangle[]
  shapes: BauhausShape[]
  lines: Line[]
  focal: { x: number; y: number }
}

// --- deterministic primitives ----------------------------------------------

function hashString(str: string): number {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < str.length; i++) {
    const ch = str.codePointAt(i) ?? 0
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  return (h1 ^ (h1 >>> 16)) >>> 0
}

/** mulberry32 PRNG → deterministic floats in [0, 1). */
function makeRng(seed: number) {
  let a = seed >>> 0
  return () => {
    // 32-bit wrap (`| 0`) is required by the mulberry32 algorithm.
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

function hexToHue(hex: string): number | null {
  const m = /^#?([\da-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const int = Number.parseInt(m[1], 16)
  const r = ((int >> 16) & 255) / 255
  const g = ((int >> 8) & 255) / 255
  const b = (int & 255) / 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  const d = max - min
  if (d === 0) return 0
  let h: number
  if (max === r) h = ((g - b) / d) % 6
  else if (max === g) h = (b - r) / d + 2
  else h = (r - g) / d + 4
  return (((h * 60) % 360) + 360) % 360
}

const css = ({ h, s, l }: HSL) => `hsl(${Math.round(((h % 360) + 360) % 360)} ${Math.round(s)}% ${Math.round(l)}%)`

function lerpHue(a: number, b: number, t: number): number {
  const d = ((b - a + 540) % 360) - 180
  return a + d * t
}

const lerp = (a: number, b: number, t: number) => a + (b - a) * t

function lerpHSL(a: HSL, b: HSL, t: number): HSL {
  return { h: lerpHue(a.h, b.h, t), s: lerp(a.s, b.s, t), l: lerp(a.l, b.l, t) }
}

// --- palette ---------------------------------------------------------------

interface Palette {
  bgA: HSL
  bgB: HSL
  dominant: HSL
  secondary: HSL
  accent: HSL
  light: HSL
}

const HARMONIES = ['analogous', 'complementary', 'triadic', 'split'] as const

function buildPalette(rng: () => number, baseHue: number): Palette {
  const range = (lo: number, hi: number) => lo + rng() * (hi - lo)
  const harmony = HARMONIES[Math.floor(rng() * HARMONIES.length)]

  let secondaryHue: number
  let accentHue: number
  switch (harmony) {
    case 'complementary':
      secondaryHue = baseHue + 25
      accentHue = baseHue + 180
      break
    case 'triadic':
      secondaryHue = baseHue + 120
      accentHue = baseHue + 240
      break
    case 'split':
      secondaryHue = baseHue + 150
      accentHue = baseHue + 210
      break
    default: // analogous
      secondaryHue = baseHue + 32
      accentHue = baseHue - 36
  }

  return {
    bgA: { h: baseHue, s: range(24, 40), l: range(13, 20) },
    bgB: { h: secondaryHue, s: range(24, 40), l: range(8, 14) },
    dominant: { h: baseHue, s: range(50, 62), l: range(44, 54) },
    secondary: { h: secondaryHue, s: range(46, 58), l: range(40, 52) },
    accent: { h: accentHue, s: range(74, 88), l: range(58, 68) },
    light: { h: baseHue, s: 30, l: 88 },
  }
}

// --- helpers shared by builders --------------------------------------------

interface Ctx {
  rng: () => number
  range: (lo: number, hi: number) => number
  int: (lo: number, hi: number) => number
  chance: (p: number) => boolean
  palette: Palette
  focal: { x: number; y: number }
  /** Quadrant kept mostly empty (diagonally opposite the focal point). */
  inNegativeSpace: (x: number, y: number) => boolean
}

/** Fan of faint lines radiating from the focal point — a subtle eye guide. */
function buildLines(ctx: Ctx, probability: number): Line[] {
  if (!ctx.chance(probability)) return []
  const { rng, range, int, focal, palette } = ctx
  const count = int(3, 6)
  const dir = rng() * Math.PI * 2
  const spread = range(0.35, 0.7)
  const stroke = css(palette.light)
  return Array.from({ length: count }, (_, k) => {
    const a = dir + (count > 1 ? (k / (count - 1) - 0.5) * spread : 0)
    const len = range(0.7, 1.2) * Math.max(VIEW_W, VIEW_H)
    return {
      x1: focal.x,
      y1: focal.y,
      x2: focal.x + Math.cos(a) * len,
      y2: focal.y + Math.sin(a) * len,
      stroke,
      opacity: range(0.08, 0.2),
      width: range(0.8, 1.6),
    }
  })
}

// --- variant builders ------------------------------------------------------

function buildMesh(ctx: Ctx): Blob[] {
  const { range, focal, palette, inNegativeSpace } = ctx
  const blobs: Blob[] = []

  // Far background wash — large, heavily blurred, low opacity.
  blobs.push({
    cx: range(0.2, 0.8) * VIEW_W,
    cy: range(0.2, 0.8) * VIEW_H,
    r: range(150, 210),
    fill: css(palette.dominant),
    opacity: range(0.3, 0.45),
    blur: 'far',
  })

  // Midground supports clustered toward the focal side (skip negative space).
  for (let i = 0; i < 2; i++) {
    let cx = 0
    let cy = 0
    for (let tries = 0; tries < 6; tries++) {
      cx = focal.x + range(-130, 130)
      cy = focal.y + range(-110, 110)
      if (!inNegativeSpace(cx, cy)) break
    }
    blobs.push({
      cx,
      cy,
      r: range(90, 140),
      fill: css(i === 0 ? palette.secondary : palette.dominant),
      opacity: range(0.45, 0.65),
      blur: 'mid',
    })
  }

  // Hero — at the focal point, accent colour, sharpest and brightest.
  blobs.push({
    cx: focal.x,
    cy: focal.y,
    r: range(70, 100),
    fill: css(palette.accent),
    opacity: range(0.8, 0.95),
    blur: 'near',
  })

  return blobs
}

function buildLowPoly(ctx: Ctx): Triangle[] {
  const { rng, range, palette, focal } = ctx
  const cell = range(58, 84)
  const variance = range(0.4, 0.62)
  const cols = Math.ceil(VIEW_W / cell) + 2
  const rows = Math.ceil(VIEW_H / cell) + 2
  const jitter = () => (rng() - 0.5) * cell * variance * 2

  // Jittered grid of points (outer ring sits beyond the canvas for full cover).
  const pts: { x: number; y: number }[][] = []
  for (let i = 0; i <= cols; i++) {
    pts[i] = []
    for (let j = 0; j <= rows; j++) {
      pts[i][j] = { x: (i - 1) * cell + jitter(), y: (j - 1) * cell + jitter() }
    }
  }

  // Gradient axis across the mesh, anchored on two palette colours.
  const angle = rng() * Math.PI * 2
  const ax = Math.cos(angle)
  const ay = Math.sin(angle)
  const colorAt = (cx: number, cy: number): string => {
    const t = ((cx / VIEW_W) * ax + (cy / VIEW_H) * ay + 1) / 2
    let col = lerpHSL(palette.bgA, palette.dominant, Math.min(1, Math.max(0, t)))
    // Brighten toward the focal point so one region clearly leads.
    const dist = Math.hypot(cx - focal.x, cy - focal.y)
    const glow = Math.max(0, 1 - dist / (0.45 * VIEW_W))
    if (glow > 0) col = lerpHSL(col, palette.accent, glow * 0.7)
    return css(col)
  }

  const tris: Triangle[] = []
  for (let i = 0; i < cols; i++) {
    for (let j = 0; j < rows; j++) {
      const a = pts[i][j]
      const b = pts[i + 1][j]
      const c = pts[i + 1][j + 1]
      const d = pts[i][j + 1]
      // Alternate the diagonal by parity for a more organic mesh.
      const diag = (i + j) % 2 === 0
      const t1 = diag ? [a, b, c] : [a, b, d]
      const t2 = diag ? [a, c, d] : [b, c, d]
      for (const t of [t1, t2]) {
        const cx = (t[0].x + t[1].x + t[2].x) / 3
        const cy = (t[0].y + t[1].y + t[2].y) / 3
        tris.push({
          points: t.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' '),
          fill: colorAt(cx, cy),
        })
      }
    }
  }
  return tris
}

function buildBauhaus(ctx: Ctx): BauhausShape[] {
  const { rng, range, int, focal, palette, inNegativeSpace } = ctx
  const fills = [palette.dominant, palette.secondary, palette.accent]
  const shapes: BauhausShape[] = []

  // Hero primitive at the focal point: large, accent-coloured.
  const heroKind = (['circle', 'ring', 'quarter'] as const)[int(0, 2)]
  const heroR = range(58, 84)
  shapes.push(makeBauhaus(heroKind, focal.x, focal.y, heroR, css(palette.accent), 0.95, rng))

  // Supporting primitives, avoiding the negative-space quadrant.
  const count = int(3, 5)
  for (let i = 0; i < count; i++) {
    let cx = 0
    let cy = 0
    for (let tries = 0; tries < 6; tries++) {
      cx = range(0.1, 0.9) * VIEW_W
      cy = range(0.1, 0.9) * VIEW_H
      if (!inNegativeSpace(cx, cy)) break
    }
    const kind = (['circle', 'ring', 'rect', 'quarter', 'bar'] as const)[int(0, 4)]
    const r = range(28, 62)
    const fill = css(fills[i % fills.length])
    shapes.push(makeBauhaus(kind, cx, cy, r, fill, range(0.7, 0.92), rng))
  }
  return shapes
}

function makeBauhaus(
  kind: BauhausShape['kind'],
  cx: number,
  cy: number,
  r: number,
  fill: string,
  opacity: number,
  rng: () => number
): BauhausShape {
  switch (kind) {
    case 'rect':
      return { kind, fill, opacity, x: cx - r, y: cy - r * 0.6, w: r * 2, h: r * 1.2, rot: Math.round(rng() * 90) }
    case 'bar': {
      const horizontal = rng() > 0.5
      const len = r * 2.4
      const thick = r * 0.36
      return {
        kind,
        fill,
        opacity,
        x: cx - (horizontal ? len : thick) / 2,
        y: cy - (horizontal ? thick : len) / 2,
        w: horizontal ? len : thick,
        h: horizontal ? thick : len,
        rot: 0,
      }
    }
    case 'quarter': {
      const rot = Math.floor(rng() * 4) * 90
      // Quarter-disc wedge centred on (cx, cy).
      const d = `M ${cx} ${cy} L ${cx + r} ${cy} A ${r} ${r} 0 0 1 ${cx} ${cy + r} Z`
      return { kind, fill, opacity, cx, cy, r, rot, d }
    }
    case 'ring':
    case 'circle':
    default:
      return { kind, fill, opacity, cx, cy, r }
  }
}

// --- entry point -----------------------------------------------------------

export function buildAbstractScene(opts: {
  seed: string
  variant?: AbstractVariant
  baseColor?: string
}): AbstractScene {
  const variant = opts.variant ?? 'mesh'
  const rng = makeRng(hashString(opts.seed || 'song7'))
  const range = (lo: number, hi: number) => lo + rng() * (hi - lo)
  const int = (lo: number, hi: number) => Math.floor(lo + rng() * (hi - lo + 1))
  const chance = (p: number) => rng() < p

  const baseHue = (opts.baseColor && hexToHue(opts.baseColor)) || rng() * 360
  const palette = buildPalette(rng, baseHue)

  // Off-centre focal point on the phi lines; negative space sits diagonally opposite.
  const PHI = [0.382, 0.618]
  const fxRatio = PHI[Math.floor(rng() * 2)]
  const fyRatio = PHI[Math.floor(rng() * 2)]
  const focal = { x: fxRatio * VIEW_W, y: fyRatio * VIEW_H }
  const inNegativeSpace = (x: number, y: number) => {
    const farX = fxRatio < 0.5 ? x > 0.62 * VIEW_W : x < 0.38 * VIEW_W
    const farY = fyRatio < 0.5 ? y > 0.62 * VIEW_H : y < 0.38 * VIEW_H
    return farX && farY
  }

  const ctx: Ctx = { rng, range, int, chance, palette, focal, inNegativeSpace }

  const scene: AbstractScene = {
    variant,
    bgFrom: css(palette.bgA),
    bgTo: css(palette.bgB),
    gradAngle: Math.round(rng() * 360),
    blobs: [],
    triangles: [],
    shapes: [],
    lines: [],
    focal,
  }

  if (variant === 'lowpoly') {
    scene.triangles = buildLowPoly(ctx)
    scene.lines = buildLines(ctx, 0.3)
  } else if (variant === 'bauhaus') {
    scene.shapes = buildBauhaus(ctx)
    scene.lines = buildLines(ctx, 0.5)
  } else {
    scene.blobs = buildMesh(ctx)
    scene.lines = buildLines(ctx, 0.6)
  }

  return scene
}
